/**
 * Per-page SEO helpers. Imperative document.head mutators that the
 * caller pairs with useEffect cleanup, so meta values don't leak across
 * client-side navigations.
 *
 * Usage:
 *   useEffect(() => {
 *     const restorers = [
 *       upsertMeta({ name: "description" }, "Page description"),
 *       upsertLink({ rel: "canonical" }, "https://tenanttrail.net/q/qld/bond"),
 *     ];
 *     return () => restorers.forEach((r) => r?.restore?.());
 *   }, [data]);
 */

/**
 * Insert or update a <meta> tag matching the given attributes. Returns a
 * { restore } object the caller calls on unmount to undo our changes
 * (either removes the tag if we created it, or restores the old content).
 */
export function upsertMeta(attrs, content) {
  if (typeof document === "undefined") return { restore: () => {} };
  const selector = Object.entries(attrs).map(([k, v]) => `[${k}="${v}"]`).join("");
  let el = document.querySelector(`meta${selector}`);
  let created = false;
  let prev = null;
  if (!el) {
    el = document.createElement("meta");
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
    created = true;
  } else {
    prev = el.getAttribute("content");
  }
  el.setAttribute("content", content);
  return {
    restore: () => {
      if (created) el.remove();
      else if (prev != null) el.setAttribute("content", prev);
    },
  };
}

/**
 * Same pattern for <link> tags (rel=canonical etc).
 */
export function upsertLink(attrs, href) {
  if (typeof document === "undefined") return { restore: () => {} };
  const selector = Object.entries(attrs).map(([k, v]) => `[${k}="${v}"]`).join("");
  let el = document.querySelector(`link${selector}`);
  let created = false;
  let prev = null;
  if (!el) {
    el = document.createElement("link");
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
    created = true;
  } else {
    prev = el.getAttribute("href");
  }
  el.setAttribute("href", href);
  return {
    restore: () => {
      if (created) el.remove();
      else if (prev != null) el.setAttribute("href", prev);
    },
  };
}

/**
 * Inject a JSON-LD <script> tag with a stable id so we can clean it up.
 */
export function injectJsonLd(id, payload) {
  if (typeof document === "undefined") return { restore: () => {} };
  document.getElementById(id)?.remove();
  const el = document.createElement("script");
  el.type = "application/ld+json";
  el.id = id;
  el.textContent = JSON.stringify(payload);
  document.head.appendChild(el);
  return { restore: () => document.getElementById(id)?.remove() };
}

/**
 * Strip markdown formatting for the JSON-LD answer field and other places
 * we need clean plain text. Minimal — handles the markdown we actually
 * emit (headings, bold, italic, lists, blockquotes, inline code).
 */
export function markdownToPlain(md) {
  if (!md) return "";
  return md
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/_([^_]+)_/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/^>\s+/gm, "")
    .replace(/^[-*+]\s+/gm, "• ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}
